function parseJsonObjectEnv(name) {
  const rawValue = process.env[name];

  if (!rawValue) {
    return {
      value: {},
      error: `${name} is missing.`,
    };
  }

  try {
    const parsedValue = JSON.parse(rawValue);

    if (
      parsedValue === null ||
      Array.isArray(parsedValue) ||
      typeof parsedValue !== "object"
    ) {
      return {
        value: {},
        error: `${name} must be a single-line JSON object.`,
      };
    }

    return {
      value: parsedValue,
      error: null,
    };
  } catch (error) {
    return {
      value: {},
      error: `${name} must be a single-line valid JSON object.`,
    };
  }
}

function normalizeBillingEnvironment(value) {
  if (value === "sandbox" || value === "production") {
    return value;
  }

  return "";
}

export function isTruthyEnv(value) {
  return String(value).toLowerCase() === "true";
}

export function getPaddleBillingConfig() {
  const billingEnvironment = normalizeBillingEnvironment(
    process.env.REACT_APP_PADDLE_BILLING_ENV
  );
  const clientToken = process.env.REACT_APP_PADDLE_BILLING_CLIENT_TOKEN || "";
  const priceMapResult = parseJsonObjectEnv("REACT_APP_PADDLE_BILLING_PRICES");
  const planNameMapResult = parseJsonObjectEnv(
    "REACT_APP_PADDLE_BILLING_PLAN_NAMES"
  );
  const planTypeMapResult = parseJsonObjectEnv(
    "REACT_APP_PADDLE_BILLING_PLAN_TYPES"
  );

  const issues = [];

  if (!billingEnvironment) {
    issues.push(
      "REACT_APP_PADDLE_BILLING_ENV must be set to \"sandbox\" or \"production\"."
    );
  }

  if (priceMapResult.error) {
    issues.push(priceMapResult.error);
  }

  if (planNameMapResult.error) {
    issues.push(planNameMapResult.error);
  }

  if (planTypeMapResult.error) {
    issues.push(planTypeMapResult.error);
  }

  if (!clientToken) {
    issues.push("REACT_APP_PADDLE_BILLING_CLIENT_TOKEN is missing.");
  } else if (
    billingEnvironment === "sandbox" &&
    !clientToken.startsWith("test_")
  ) {
    issues.push(
      "Sandbox Paddle Billing client tokens must start with \"test_\"."
    );
  } else if (
    billingEnvironment === "production" &&
    !clientToken.startsWith("live_")
  ) {
    issues.push(
      "Production Paddle Billing client tokens must start with \"live_\"."
    );
  }

  const priceMap = priceMapResult.value;
  const planNameMap = planNameMapResult.value;
  const planTypeMap = planTypeMapResult.value;
  const priceIds = Object.values(priceMap).filter(Boolean);

  if (priceIds.length === 0) {
    issues.push("REACT_APP_PADDLE_BILLING_PRICES does not contain any price IDs.");
  }

  priceIds.forEach((priceId) => {
    if (!planNameMap[priceId]) {
      issues.push(
        `Missing plan name mapping for Paddle Billing price "${priceId}".`
      );
    }

    if (!planTypeMap[priceId]) {
      issues.push(
        `Missing plan type mapping for Paddle Billing price "${priceId}".`
      );
    }
  });

  return {
    billingEnv: billingEnvironment,
    clientToken,
    priceMap,
    priceMapError: priceMapResult.error,
    configuredPriceIds: priceIds,
    planNameMap,
    planTypeMap,
    isBillingConfigured: issues.length === 0,
    configIssues: issues,
    configError: issues[0] || "",
  };
}

let billingPaddleClientPromise;
const BILLING_PADDLE_SCRIPT_URL = "https://cdn.paddle.com/paddle/v2/paddle.js";
const billingEventListeners = new Set();

function notifyBillingEventListeners(event) {
  billingEventListeners.forEach((listener) => {
    try {
      listener(event);
    } catch (error) {
      // Ignore listener failures so Paddle event handling still completes.
    }
  });
}

function getBillingTransactionId(checkout) {
  const transactionId = checkout?.transaction_id || checkout?.transactionId;

  if (transactionId) {
    return transactionId;
  }

  const checkoutUrl = checkout?.checkout_url || checkout?.checkoutUrl;

  if (!checkoutUrl) {
    return "";
  }

  try {
    const url = new URL(checkoutUrl);
    return url.searchParams.get("_ptxn") || "";
  } catch (error) {
    return "";
  }
}

function initializeBillingPaddleInstance(billingConfig) {
  const paddle = window.Paddle;

  if (!paddle) {
    throw new Error("Paddle Billing failed to load.");
  }

  if (billingConfig.billingEnv === "sandbox") {
    paddle.Environment.set("sandbox");
  }

  paddle.Initialize({
    token: billingConfig.clientToken,
    eventCallback: (event) => {
      notifyBillingEventListeners(event);
    },
    checkout: {
      settings: {
        displayMode: "overlay",
        frameStyle: "height: 600px",
        theme: "light",
        locale: "en",
        allowLogout: "false",
      },
    },
  });

  return paddle;
}

export async function getPaddleBillingClient() {
  const billingConfig = getPaddleBillingConfig();

  if (!billingConfig.isBillingConfigured) {
    throw new Error(
      billingConfig.configError ||
        "Paddle Billing is not configured correctly for this frontend."
    );
  }

  if (!billingPaddleClientPromise) {
    billingPaddleClientPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector(
        `script[src="${BILLING_PADDLE_SCRIPT_URL}"]`
      );

      const handleReady = () => {
        try {
          resolve(initializeBillingPaddleInstance(billingConfig));
        } catch (error) {
          reject(error);
        }
      };

      if (existingScript) {
        if (window.Paddle) {
          handleReady();
          return;
        }

        existingScript.addEventListener("load", handleReady, { once: true });
        existingScript.addEventListener(
          "error",
          () => reject(new Error("Unable to load Paddle Billing")),
          { once: true }
        );
        return;
      }

      const script = document.createElement("script");
      script.src = BILLING_PADDLE_SCRIPT_URL;
      script.async = true;
      script.addEventListener("load", handleReady, { once: true });
      script.addEventListener(
        "error",
        () => reject(new Error("Unable to load Paddle Billing")),
        { once: true }
      );
      document.body.appendChild(script);
    }).catch((error) => {
      billingPaddleClientPromise = null;
      throw error;
    });
  }

  return billingPaddleClientPromise;
}

export function subscribeToPaddleBillingEvents(listener) {
  billingEventListeners.add(listener);

  return () => {
    billingEventListeners.delete(listener);
  };
}

export async function openPaddleBillingCheckout(checkout) {
  const transactionId = getBillingTransactionId(checkout);

  if (!transactionId) {
    throw new Error("Paddle Billing transaction ID was not returned");
  }

  const paddle = await getPaddleBillingClient();

  if (!paddle?.Checkout?.open) {
    throw new Error("Paddle Billing checkout is unavailable.");
  }

  paddle.Checkout.open({
    transactionId,
  });
}
